/**
 * ChatCaptureConsentBanner.js — one-time per-user consent banner for chat
 * capture (piece 1 of Commit D plan).
 *
 * Render gate:
 *   chatCaptureEnabled === false              → render nothing
 *   userId missing                            → render nothing
 *   hasConsent(userId) is true or false       → render nothing (already decided)
 *   hasConsent(userId) is null (never asked)  → render the banner
 *
 * Two actions write true/false under the per-user localStorage key and
 * dismiss. Backend kill switch is the real gate on capture behavior —
 * this banner is the UX / disclosure layer on top of it.
 *
 * Defensive: if captureConsent.setConsent silently no-ops (localStorage
 * disabled), the local dismissed state still hides the banner for this
 * session. On next reload it will reappear — acceptable v1 tradeoff since
 * localStorage-disabled users are rare.
 */

import React, { useEffect, useState } from 'react';
import { colors } from './AppLayout';
import { hasConsent, setConsent } from '../utils/captureConsent';


function ChatCaptureConsentBanner({ userId, chatCaptureEnabled, onConsentSet = null }) {
  // Three states:
  //   null  — haven't checked yet (or showing)
  //   true  — dismissed this session
  const [dismissed, setDismissed] = useState(false);
  // Existing stored choice (true / false / null). We read it once on mount
  // and whenever userId changes — captureConsent is per-user.
  const [stored, setStored] = useState(() => hasConsent(userId));

  useEffect(() => {
    setStored(hasConsent(userId));
    setDismissed(false);
  }, [userId]);

  // Render gate
  if (!chatCaptureEnabled) return null;
  if (userId == null) return null;
  if (dismissed) return null;
  if (stored === true || stored === false) return null;

  const handleChoice = (allowed) => {
    setConsent(userId, allowed);
    setDismissed(true);
    if (onConsentSet) onConsentSet(allowed);
  };

  const primaryBtn = {
    background: colors.electricBlue,
    border: `1px solid ${colors.electricBlue}`,
    color: colors.white,
    padding: '6px 14px',
    borderRadius: '6px',
    fontSize: 'var(--font-size-sm, 13px)',
    fontWeight: 600,
    cursor: 'pointer',
  };

  const secondaryBtn = {
    background: 'transparent',
    border: `1px solid ${colors.mediumGray}`,
    color: colors.darkText,
    padding: '6px 14px',
    borderRadius: '6px',
    fontSize: 'var(--font-size-sm, 13px)',
    fontWeight: 500,
    cursor: 'pointer',
  };

  return (
    <div
      role="region"
      aria-label="Chat capture consent"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '16px',
        padding: '14px 18px',
        marginBottom: '20px',
        border: `1px solid ${colors.mediumGray}`,
        backgroundColor: '#F5F8FF',
        borderRadius: '10px',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
           stroke={colors.electricBlue} strokeWidth="2" strokeLinecap="round"
           strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '2px' }}>
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="16" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12.01" y2="8" />
      </svg>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 'var(--font-size-sm, 13px)',
          fontWeight: 600,
          color: colors.darkText,
          marginBottom: '4px',
        }}>
          Wingman can learn from your conversations
        </div>
        <div style={{
          fontSize: 'var(--font-size-sm, 13px)',
          color: colors.textMuted,
          lineHeight: 1.5,
        }}>
          Mentioning a fact like "Parker raised prices 8%" can be saved to
          your knowledge base. You'll always see what's captured and can
          retract any of it.
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px', flexShrink: 0, alignItems: 'center' }}>
        <button type="button" style={secondaryBtn} onClick={() => handleChoice(false)}>
          Not now
        </button>
        <button type="button" style={primaryBtn} onClick={() => handleChoice(true)}>
          Allow capture
        </button>
      </div>
    </div>
  );
}

export default ChatCaptureConsentBanner;
